import { useState, useEffect } from 'react';
import { createPublicoTurma, findPublicoTurma } from 'API/apiAcoes';

export const usePublicoTurma = (turmaId: string | null) => {
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loadingPublico, setLoadingPublico] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [ambitoEstadual, setAmbitoEstadual] = useState<boolean>(false);
  const [ambitoMunicipal, setAmbitoMunicipal] = useState<boolean>(false);
  const [selectedLotacoes, setSelectedLotacoes] = useState<number[]>([]);
  const [selectedOrgaos, setSelectedOrgaos] = useState<number[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<number[]>([]);
  const [selectedFuncoes, setSelectedFuncoes] = useState<number[]>([]);

  useEffect(() => {
    if (turmaId) {
      carregarPublicoTurma(turmaId);
    }
  }, [turmaId]);

  const carregarPublicoTurma = async (idTurma: string) => {
    setLoadingPublico(true);

    try {
      const response = await findPublicoTurma(idTurma);

      if (response) {
        const ambitos = Array.isArray(response.ambitos)
          ? response.ambitos.map((item) => Number(item))
          : [];
        const existeEstadual = ambitos.includes(1);
        const existeMunicipal = ambitos.includes(2);

        setAmbitoEstadual(existeEstadual);
        setAmbitoMunicipal(existeMunicipal);

        setSelectedLotacoes(Array.isArray(response.lotacoes) ? response.lotacoes : []);
        setSelectedOrgaos(Array.isArray(response.orgaos_atuacao) ? response.orgaos_atuacao : []);
        setSelectedAreas(Array.isArray(response.areas_atuacao) ? response.areas_atuacao : []);
        setSelectedFuncoes(Array.isArray(response.funcoes) ? response.funcoes : []);
      }
    } catch (error) {
      setMsgError('Erro ao carregar o publico da turma');
    } finally {
      setLoadingPublico(false);
    }
  };

  const handleChangeSelect = (value: (string | number)[], state: string) => {
    const numericIds = value.map(id => Number(id));

    switch (state) {
      case 'lotacoes':
        setSelectedLotacoes(numericIds);
        break;

      case 'orgaos':
        setSelectedOrgaos(numericIds);
        break;

      case 'areas':
        setSelectedAreas(numericIds);
        break;

      case 'funcoes':
        setSelectedFuncoes(numericIds);
        break;

      default:
        break;
    }
  };

  const handleChangeCheckMunicipal = (event: any) => {
    setAmbitoMunicipal(event.target.checked);
  };

  const handleChangeCheckEstadual = (event: any) => {
    setAmbitoEstadual(event.target.checked);
  };

  const validade = () => {
    if (!ambitoEstadual && !ambitoMunicipal) {
      setMsgError('Marque pelo menos uma opcao entre Municipal e Estadual');
      return false;
    }

    if (ambitoEstadual && selectedLotacoes.length === 0) {
      setMsgError('Selecione pelo menos uma lotacao');
      return false;
    }

    if (ambitoMunicipal && selectedOrgaos.length === 0) {
      setMsgError('Selecione pelo menos um orgao de atuacao');
      return false;
    }

    if (selectedAreas.length === 0) {
      setMsgError('Selecione pelo menos uma area de atuacao');
      return false;
    }

    if (selectedFuncoes.length === 0) {
      setMsgError('Selecione pelo menos uma funcao');
      return false;
    }

    return true;
  };

  const savePublicoTurma = async (): Promise<boolean> => {
    if (!validade()) {
      return false;
    }

    if (!turmaId) {
      setMsgError('Erro ao encontrar turma selecionada');
      return false;
    }

    setLoadingSave(true);

    try {
      const ambitos: number[] = [];

      if (ambitoEstadual) {
        ambitos.push(1);
      }

      if (ambitoMunicipal) {
        ambitos.push(2);
      }

      const payload = {
        ambitos,
        orgaos_atuacao: selectedOrgaos,
        lotacoes: selectedLotacoes,
        areas_atuacao: selectedAreas,
        funcoes: selectedFuncoes
      };

      const response = await createPublicoTurma(turmaId, payload);

      if (!response.success) {
        setMsgError('Nao foi possivel cadastrar o publico alvo da turma');
        return false;
      }

      setMsgSuccess('Publico alvo da turma cadastrado com sucesso');
      return true;
    } catch (error) {
      setMsgError('Nao foi possivel cadastrar o publico alvo da turma');
      return false;
    } finally {
      setLoadingSave(false);
    }
  };

  return {
    msgError,
    msgSuccess,
    loadingPublico,
    loadingSave,
    ambitoEstadual,
    setAmbitoEstadual,
    ambitoMunicipal,
    setAmbitoMunicipal,
    selectedLotacoes,
    setSelectedLotacoes,
    selectedOrgaos,
    setSelectedOrgaos,
    selectedAreas,
    setSelectedAreas,
    selectedFuncoes,
    setSelectedFuncoes,
    handleChangeSelect,
    handleChangeCheckMunicipal,
    handleChangeCheckEstadual,
    savePublicoTurma,
  };
};
